import { FileText, ExternalLink, FlaskConical, ArrowRight } from "lucide-react";

const WEBSITE_URL = "https://www.inducext.com/";
const BROCHURE_URL =
    "https://drive.google.com/file/d/1nL97CndBRP1nYOxPcBs3scrW_HdQI37A/view";
const STUDY_URL = "https://clinicaltrials.gov/study/NCT06662266";

const SIZES = ["1.5 cc", "3.0 cc", "10.0 cc"];

const RESOURCES = [
    {
        id: "brochure",
        icon: FileText,
        eyebrow: "Product brochure",
        title: "Review product details and specifications.",
        cta: "View brochure",
        href: BROCHURE_URL,
    },
    {
        id: "manufacturer",
        icon: ExternalLink,
        eyebrow: "Manufacturer website",
        title: "Explore InduceXT product information.",
        cta: "Visit manufacturer",
        href: WEBSITE_URL,
    },
    {
        id: "study",
        icon: FlaskConical,
        eyebrow: "Clinical study",
        title: "Foot & ankle fusion study",
        subtitle: "NCT06662266",
        note: "A prospective Induce XT foot & ankle fusion study is registered but not yet reported.",
        cta: "View study registry",
        href: STUDY_URL,
    },
];

/**
 * Product resources section — available sizes + three purposeful resource cards.
 * Shared by /inducext and /for-surgeons.
 */
export default function InduceXTResources() {
    return (
        <section
            data-testid="inducext-resources-section"
            className="border-b border-slate-200 bg-white"
        >
            <div className="max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-20">
                {/* Heading */}
                <div className="max-w-3xl">
                    <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
                        Sizes &amp; Resources
                    </p>
                    <h2
                        data-testid="inducext-resources-heading"
                        className="mt-3 font-display text-3xl md:text-[32px] font-semibold tracking-tight text-slate-900 leading-[1.15]"
                    >
                        Available sizes.
                    </h2>
                    <p className="mt-3 text-[15px] md:text-base text-slate-600 leading-relaxed">
                        InduceXT is offered in three sizes to fit a range of
                        procedures.
                    </p>
                </div>

                {/* Sizes card */}
                <div
                    data-testid="inducext-sizes-card"
                    className="mt-8 rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.08)] overflow-hidden"
                >
                    <div className="px-6 md:px-8 pt-6 md:pt-7 pb-4">
                        <h3 className="font-display text-base md:text-lg font-semibold text-slate-900">
                            Available sizes
                        </h3>
                    </div>
                    <ul className="border-t border-slate-200 divide-y divide-slate-100">
                        {SIZES.map((size) => {
                            const rowId = size
                                .toLowerCase()
                                .replace(/[^a-z0-9]+/g, "-");
                            return (
                                <li
                                    key={size}
                                    data-testid={`inducext-size-row-${rowId}`}
                                    className="px-6 md:px-8 py-5 text-slate-900 font-medium tabular-nums"
                                >
                                    {size}
                                </li>
                            );
                        })}
                    </ul>
                </div>

                {/* Resource cards */}
                <div
                    data-testid="inducext-resources-panel"
                    className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4"
                >
                    {RESOURCES.map((r) => {
                        const Icon = r.icon;
                        return (
                            <article
                                key={r.id}
                                data-testid={`inducext-resource-${r.id}`}
                                className="flex flex-col rounded-2xl border border-slate-200 bg-white p-6 md:p-7 hover:border-slate-400 transition-colors"
                            >
                                <div className="flex items-center gap-2 text-slate-500">
                                    <Icon size={16} className="text-slate-500" />
                                    <p className="text-[11px] font-medium uppercase tracking-[0.14em]">
                                        {r.eyebrow}
                                    </p>
                                </div>
                                <p className="mt-4 font-display text-lg font-semibold text-slate-900 leading-snug">
                                    {r.title}
                                </p>
                                {r.subtitle && (
                                    <p className="mt-1.5 text-sm font-medium text-slate-700 tabular-nums">
                                        {r.subtitle}
                                    </p>
                                )}
                                {r.note && (
                                    <p className="mt-3 text-[13px] text-slate-500 leading-relaxed border-l-2 border-slate-200 pl-3">
                                        {r.note}
                                    </p>
                                )}
                                <div className="mt-auto pt-6">
                                    <a
                                        href={r.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`inducext-resource-${r.id}-btn`}
                                        className="inline-flex w-full md:w-auto items-center justify-center gap-2 bg-slate-900 text-white text-sm font-medium px-5 py-3 rounded-md hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2 transition-colors"
                                    >
                                        {r.cta}
                                        <ArrowRight size={14} />
                                    </a>
                                </div>
                            </article>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
